import React, { useState, useEffect } from 'react';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { globalCss as global } from '@uhg-abyss/web/tools/styled';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { SideNavV3 } from './SideNavV3';
import { SideNavProvider } from './SideNavProvider';
import { PageHeadingV3 } from './PageHeadingV3';
import { APIDetailsV3 } from './APIDetailsV3';
import { ErrorCodesV3 } from './ErrorCodesV3';
import { useDocumentationV3 } from '@src/hooks/useDocumentationV3';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { event } from '@uhg-abyss/web/tools/event';
import { useSelector } from 'react-redux';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { LagoonContext } from '@src/AppContext';
import { ServiceSandboxV3 } from '@src/common/ServiceSandboxV3';

const UpdatedSandboxStyle = global({
  '.abyss-table-cell': {
    wordBreak: 'unset !important',
  },
  '.abyss-box-root': {
    alignItems: 'flex-start',
  },
});

const StyledContent = (props) => {
  return (
    <Grid.Col
      css={{
        'abyss-grid-col': {
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
        },
      }}
      {...props}
    />
  );
};

const INITIAL_CURRENT_API = {
  description: '',
  isDisabled: false,
  method: '',
  operationId: '',
  operationSlug: '',
  parameters: [],
  path: '',
  produces: [],
  responses: [],
  summary: '',
  tags: [],
  schema: {},
};

export const DocumentationV3 = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const [sideNavData, setSideNavData] = useState([]);
  const [isMarkDown, setIsMarkDown] = useState(false);

  const [currentApi, setCurrentApi] = useState(INITIAL_CURRENT_API);

  const { navigate } = useRouter();
  const { service, slug } = useParams();

  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });

  const handleTabChange = (tab) => {
    const interactionName = `${service} ${currentApi.method} ${currentApi.summary}`;
    const interactionValueList = ['api sandbox', 'api details', 'error codes'];
    const interactionValue = interactionValueList[tab];
    const interactionContext = 'tab clicks';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  const getCurrentApi = (data, slugParm) => {
    let returnVal;
    data.pathsByTag.forEach((listByTag) => {
      listByTag.paths.forEach((path) => {
        if (path.operationSlug.toLowerCase() === slugParm?.toLowerCase()) {
          returnVal = path;
        }
      });
    });

    if (returnVal) {
      setCurrentApi({ ...returnVal, schema: data?.components?.schemas || {} });
      if (slugParm) {
        event('DOCUMENTATION_PAGE_LOAD', {
          service,
          slug: slugParm,
          dsUserInfo,
          userInfo,
        });
      }
    }
  };

  const handleCompleted = React.useCallback(
    (data) => {
      if (data.pathsByTag && data.pathsByTag[0]) {
        const returnVal = { ...data.pathsByTag[0].paths[0] };
        //returnVal logic
        getCurrentApi(data, slug || returnVal.operationSlug);
        if (!slug && returnVal.operationSlug) {
          navigate(`/documentation/${service}/${returnVal.operationSlug}`, {
            replace: true,
          });
        }
      }
    },
    [slug, service, navigate]
  );

  const [documentation, getDocumentation] = useDocumentationV3({
    onCompleted: handleCompleted,
    onError: () => {
      setCurrentApi(INITIAL_CURRENT_API);
    },
  });

  const { data } = documentation;

  useEffect(() => {
    if (!data?.service) return;

    const isCurrentServiceData =
      data.service === service || data.service.startsWith(`${service}/`);
    if (!isCurrentServiceData) return;

    if (slug) {
      getCurrentApi(data, slug);
    } else if (data.pathsByTag?.[0]?.paths?.[0]) {
      const firstSlug = data.pathsByTag[0].paths[0].operationSlug;
      if (firstSlug) {
        navigate(`/documentation/${service}/${firstSlug}`, { replace: true });
      }
    }
  }, [slug, data, service]);

  useEffect(() => {
    if (service === 'provideraccess') {
      setIsMarkDown(true);
      return;
    }

    if (service && lagoonContext) {
      if (service === 'superconnector') {
        const navData = lagoonContext
          ?.getTable('technical-documentation')
          .getRow();
        const filteredNavData = navData.filter((item) => {
          return item.active;
        });
        if (filteredNavData && filteredNavData.length > 0) {
          if (slug === undefined && filteredNavData[0].id) {
            navigate(`/documentation/${service}/${filteredNavData[0].id}`);
          }
          setSideNavData(filteredNavData);

          const currentNavItem = filteredNavData.find((item) => {
            return item.id === slug;
          });
          if (currentNavItem) {
            setIsMarkDown(currentNavItem.markdown || false);
          }
        }
      }
    }
  }, [service, slug, lagoonContext, navigate]);

  useEffect(() => {
    if (service) {
      if (service === 'superconnector') {
        // superconnector fetches per-slug, so always re-fetch on slug change
        if (slug !== undefined) {
          setCurrentApi(INITIAL_CURRENT_API);
          getDocumentation({ userInfo, service: `${service}/${slug}` });
        }
      } else if (!data || data.service !== service) {
        // For services like provideraccess the full spec is one JSON file —
        // only fetch once (no slug required). Subsequent slug changes are
        // handled by the useEffect([slug, data]) which calls getCurrentApi directly.
        // Also re-fetch if the Redux store holds data from a different service.
        setCurrentApi(INITIAL_CURRENT_API);
        getDocumentation({ userInfo, service });
      }
    }
  }, [slug, service]);

  return (
    <LoadingOverlay
      label={service}
      isLoading={documentation.loading}
      isError={documentation.error}
    >
      <Grid>
        <StyledContent span={3}>
          {service === 'superconnector' ? (
            <SideNavV3>
              <SideNavV3.Link
                service={service}
                data={sideNavData}
                currentTags={currentApi.tags}
              />
            </SideNavV3>
          ) : service === 'provideraccess' ? (
            <SideNavProvider>
              <SideNavProvider.Link
                service={service}
                data={data}
                currentTags={currentApi.tags}
              />
            </SideNavProvider>
          ) : null}
        </StyledContent>
        <StyledContent span={9}>
          <div>
            <PageHeadingV3
              info={data?.info}
              currentApi={currentApi}
              service={service}
              slug={slug}
            />
            <Tabs
              onTabChange={handleTabChange}
              css={{
                '.abyss-tabs-tab-content': { fontSize: '15px' },
              }}
            >
              <Tabs.Tab label="API Sandbox">
                <UpdatedSandboxStyle />
                <ServiceSandboxV3
                  currentApi={currentApi}
                  swagger={data}
                  service={service}
                />
              </Tabs.Tab>
              <Tabs.Tab label="API Details">
                <APIDetailsV3 currentApi={currentApi} isMarkdown={isMarkDown} />
              </Tabs.Tab>
              <Tabs.Tab label="Error Codes">
                <ErrorCodesV3 currentApi={currentApi} />
              </Tabs.Tab>
            </Tabs>
          </div>
        </StyledContent>
      </Grid>
    </LoadingOverlay>
  );
};
